/**
 * Agent Types Constants
 * 
 * SAFE CLEANUP: Centralized canonical values for agent configuration.
 * DO NOT modify these values without verifying all consumers.
 * 
 * This file is the SINGLE SOURCE OF TRUTH for:
 * - accepted_inputs values
 * - run_modal_variant values
 * - capability mode values
 */

/**
 * Canonical accepted_inputs values
 * Used across: RunAgentModal, Command, AgentControlCentre, AgentEditor
 */
export const ACCEPTED_INPUT_TYPES = {
  // Text inputs
  TEXT: 'text',
  JSON: 'json',
  URL: 'url',
  
  // Document types
  FILE: 'file',
  FILE_PDF: 'file_pdf',
  FILE_IMAGE: 'file_image',
  FILE_EXCEL: 'file_excel',
  FILE_DOCUMENT: 'file_document',
  FILE_VIDEO: 'file_video',
  
  // Audio/Video
  AUDIO: 'audio',
  VIDEO: 'video',
  VOICE: 'voice',
};

/**
 * Check if an input type represents a file upload requirement
 * @param {string} inputType - The input type to check
 * @returns {boolean}
 */
export const isFileInputType = (inputType) => {
  const fileTypes = [
    ACCEPTED_INPUT_TYPES.FILE,
    ACCEPTED_INPUT_TYPES.FILE_PDF,
    ACCEPTED_INPUT_TYPES.FILE_IMAGE,
    ACCEPTED_INPUT_TYPES.FILE_EXCEL,
    ACCEPTED_INPUT_TYPES.FILE_DOCUMENT,
    ACCEPTED_INPUT_TYPES.FILE_VIDEO,
  ];
  return fileTypes.includes(inputType);
};

/**
 * Check if an input type represents audio/video
 * @param {string} inputType - The input type to check
 * @returns {boolean}
 */
export const isAudioVideoInputType = (inputType) => {
  return [
    ACCEPTED_INPUT_TYPES.AUDIO,
    ACCEPTED_INPUT_TYPES.VIDEO,
    ACCEPTED_INPUT_TYPES.VOICE,
  ].includes(inputType);
};

/**
 * Run modal variant values
 * Determines which UI variant to show when running an agent
 */
export const RUN_MODAL_VARIANTS = {
  TEXT_ONLY: 'fixed_intent_text_only',
  FILE_UPLOAD: 'fixed_intent_file_upload',
  AUDIO_VIDEO: 'fixed_intent_audio_video',
  VOICE_S2S: 'voice_s2s_informational',
};

/**
 * Agent capability modes (for marketplace filtering)
 */
export const CAPABILITY_MODES = {
  TEXT_ONLY: 'text_only',
  AUTOMATION_READY: 'automation_ready',
  INTEGRATION_DEPENDENT: 'integration_dependent',
};

/**
 * Default accepted_inputs when agent has none specified
 */
export const DEFAULT_ACCEPTED_INPUTS = ['text'];

/**
 * Get accepted_inputs from an agent, with safe fallback
 * @param {object} agent - The agent object
 * @returns {string[]} Array of accepted input types
 */
export const getAgentAcceptedInputs = (agent) => {
  if (!agent) return DEFAULT_ACCEPTED_INPUTS;
  
  const inputs = agent.accepted_inputs;
  if (Array.isArray(inputs) && inputs.length > 0) {
    return inputs;
  }
  
  return DEFAULT_ACCEPTED_INPUTS;
};

/**
 * Derive what the agent requires based on its accepted_inputs
 * @param {object} agent - The agent object
 * @returns {object} Object with boolean flags for each requirement type
 */
export const deriveAgentRequirements = (agent) => {
  const inputs = getAgentAcceptedInputs(agent);
  
  return {
    needsText: inputs.includes(ACCEPTED_INPUT_TYPES.TEXT),
    needsFile: inputs.some(isFileInputType),
    needsAudioVideo: inputs.some(isAudioVideoInputType),
    needsUrl: inputs.includes(ACCEPTED_INPUT_TYPES.URL),
    needsJson: inputs.includes(ACCEPTED_INPUT_TYPES.JSON),
    acceptedInputs: inputs,
  };
};
